import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    MenuItem,
    Stack,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    Autocomplete,
    Avatar,
    Typography,
    IconButton,
    Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { createAnnouncement as createAnnouncementAction } from '../controllers/announcementsController';
import { searchClubEvents } from '../controllers/clubEventsController';
import { fetchAllProjects } from '../controllers/projectsController';
import { fetchAllMeetings } from '../controllers/meetingsController';
import { fetchAllNews } from '../controllers/newsController';
import { searchGroups } from '../controllers/groupManagementController';
import { searchMembers, fetchAllMembers } from '../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../api/httpClientGroups';
import { searchEmailTemplates } from '../controllers/emailTemplateController';
import RichTextEditor from './RichTextEditor';
import { toast } from 'react-toastify';

const initialCreateForm = {
    title: '',
    content: '',
    category: 0,
    relatedEntityId: null,
    relatedEntity: null,
    sendChannels: [8],
    targetType: 0,
    targetData: '',
    visibility: 0,
    automationType: 0,
    automationRule: '',
    expireAt: '',
    scheduledOnceAt: '',
    recurrenceType: 'weekly',
    recurrenceDayOfWeek: 'MON',
    recurrenceDayOfMonth: 1,
    recurrenceMonth: 1,
    emailTemplateId: null,
    emailTemplate: null,
    isPublished: false,
    publishAt: '',
    coverImage: null,
    attachments: [],
};

const CreateAnnouncementDialog = ({ open, onClose, onSuccess }) => {
    const [createForm, setCreateForm] = useState(initialCreateForm);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');
    const [eventQuery, setEventQuery] = useState('');
    const [eventOptions, setEventOptions] = useState([]);
    const [eventLoading, setEventLoading] = useState(false);
    const [projectQuery, setProjectQuery] = useState('');
    const [projectOptions, setProjectOptions] = useState([]);
    const [projectLoading, setProjectLoading] = useState(false);
    const [meetingQuery, setMeetingQuery] = useState('');
    const [meetingOptions, setMeetingOptions] = useState([]);
    const [meetingLoading, setMeetingLoading] = useState(false);
    const [newsQuery, setNewsQuery] = useState('');
    const [newsOptions, setNewsOptions] = useState([]);
    const [newsLoading, setNewsLoading] = useState(false);
    const [newMemberQuery, setNewMemberQuery] = useState('');
    const [newMemberOptions, setNewMemberOptions] = useState([]);
    const [newMemberLoading, setNewMemberLoading] = useState(false);
    const [groupQuery, setGroupQuery] = useState('');
    const [groupOptions, setGroupOptions] = useState([]);
    const [groupLoading, setGroupLoading] = useState(false);
    const [memberQuery, setMemberQuery] = useState('');
    const [memberOptions, setMemberOptions] = useState([]);
    const [memberLoading, setMemberLoading] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [emailTemplateQuery, setEmailTemplateQuery] = useState('');
    const [emailTemplateOptions, setEmailTemplateOptions] = useState([]);
    const [emailTemplateLoading, setEmailTemplateLoading] = useState(false);
    const [coverImagePreview, setCoverImagePreview] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(0);

    const isEmailChannelSelected = useMemo(() => createForm.sendChannels.includes(1), [createForm.sendChannels]);

    const handleClose = () => {
        setCreateForm(initialCreateForm);
        setCoverImagePreview(null);
        setFileInputKey((prev) => prev + 1);
        setCreateError('');
        setSelectedMembers([]);
        onClose();
    };

    useEffect(() => {
        if (!open) {
            setCreateForm(initialCreateForm);
            setCoverImagePreview(null);
            setFileInputKey((prev) => prev + 1);
            setCreateError('');
            setSelectedMembers([]);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (Number(createForm.category) !== 1) {
            setEventOptions([]);
            return;
        }
        let active = true;
        setEventLoading(true);
        searchClubEvents({ keyword: eventQuery?.trim() || undefined, pageNumber: 1, pageSize: eventQuery?.trim() ? 10 : 50 })
            .then((res) => {
                console.log('[Etkinlik] searchClubEvents response:', res);
                const { data } = res;
                if (active) setEventOptions(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (active) setEventOptions([]);
            })
            .finally(() => {
                if (active) setEventLoading(false);
            });
        return () => {
            active = false;
        };
    }, [open, createForm.category, eventQuery]);

    useEffect(() => {
        if (!open) return;
        if (Number(createForm.category) !== 2) {
            setProjectOptions([]);
            return;
        }
        let active = true;
        setProjectLoading(true);
        fetchAllProjects({ pageNumber: 1, pageSize: 50 })
            .then((res) => {
                console.log('[Proje] fetchAllProjects response:', res);
                const { data } = res;
                if (active) setProjectOptions(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (active) setProjectOptions([]);
            })
            .finally(() => {
                if (active) setProjectLoading(false);
            });
        return () => {
            active = false;
        };
    }, [open, createForm.category]);

    useEffect(() => {
        if (!open) return;
        if (Number(createForm.category) !== 8) {
            setMeetingOptions([]);
            return;
        }
        let active = true;
        setMeetingLoading(true);
        fetchAllMeetings({
            pageNumber: 1,
            pageSize: meetingQuery?.trim() ? 10 : 50,
            searchTerm: meetingQuery?.trim() || undefined,
        })
            .then((res) => {
                console.log('[Haftalık Toplantı] fetchAllMeetings response:', res);
                const { data } = res;
                if (active) setMeetingOptions(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (active) setMeetingOptions([]);
            })
            .finally(() => {
                if (active) setMeetingLoading(false);
            });
        return () => {
            active = false;
        };
    }, [open, createForm.category, meetingQuery]);

    useEffect(() => {
        if (!open) return;
        if (Number(createForm.category) !== 9) {
            setNewsOptions([]);
            return;
        }
        let active = true;
        setNewsLoading(true);
        fetchAllNews({ pageNumber: 1, pageSize: 50 })
            .then((res) => {
                console.log('[Haber] fetchAllNews response:', res);
                const { data } = res;
                if (active) setNewsOptions(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (active) setNewsOptions([]);
            })
            .finally(() => {
                if (active) setNewsLoading(false);
            });
        return () => {
            active = false;
        };
    }, [open, createForm.category]);

    useEffect(() => {
        if (!open) return;
        if (Number(createForm.category) !== 6) {
            setNewMemberOptions([]);
            return;
        }
        let active = true;
        setNewMemberLoading(true);
        const loadMembers = newMemberQuery?.trim()
            ? searchMembers(newMemberQuery.trim()).then((data) => (Array.isArray(data) ? data : []))
            : fetchAllMembers({ pageNumber: 1, pageSize: 50 }).then(({ data }) => (Array.isArray(data) ? data : []));
        loadMembers
            .then((data) => {
                if (active) setNewMemberOptions(data);
            })
            .catch(() => {
                if (active) setNewMemberOptions([]);
            })
            .finally(() => {
                if (active) setNewMemberLoading(false);
            });
        return () => {
            active = false;
        };
    }, [open, createForm.category, newMemberQuery]);

    useEffect(() => {
        if (!open) return;
        if (Number(createForm.targetType) !== 1) return;
        if (!groupQuery || groupQuery.length < 2) {
            setGroupOptions([]);
            return;
        }
        let active = true;
        setGroupLoading(true);
        searchGroups({ keyword: groupQuery })
            .then((data) => {
                if (active) setGroupOptions(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (active) setGroupOptions([]);
            })
            .finally(() => {
                if (active) setGroupLoading(false);
            });
        return () => {
            active = false;
        };
    }, [open, createForm.targetType, groupQuery]);

    useEffect(() => {
        if (!open) return;
        if (Number(createForm.targetType) !== 2) return;
        if (!memberQuery || memberQuery.length < 2) {
            setMemberOptions([]);
            return;
        }
        let active = true;
        setMemberLoading(true);
        searchMembers(memberQuery)
            .then((data) => {
                if (active) setMemberOptions(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (active) setMemberOptions([]);
            })
            .finally(() => {
                if (active) setMemberLoading(false);
            });
        return () => {
            active = false;
        };
    }, [open, createForm.targetType, memberQuery]);

    useEffect(() => {
        if (!open) return;
        setCreateForm((f) => ({ ...f, relatedEntityId: null, relatedEntity: null }));
    }, [open, createForm.category]);

    useEffect(() => {
        if (!open) return;
        if (Number(createForm.targetType) === 1 || Number(createForm.targetType) === 2) {
            if (createForm.visibility !== 0) setCreateForm((f) => ({ ...f, visibility: 0 }));
        }
    }, [open, createForm.targetType]);

    useEffect(() => {
        if (!open) return;
        if (Number(createForm.targetType) !== 2 && selectedMembers.length) {
            setSelectedMembers([]);
        }
    }, [open, createForm.targetType, selectedMembers.length]);

    useEffect(() => {
        if (!open) return;
        if (!isEmailChannelSelected) {
            setCreateForm((f) => ({ ...f, emailTemplateId: null, emailTemplate: null }));
            setEmailTemplateOptions([]);
            setEmailTemplateQuery('');
            return;
        }
    }, [open, isEmailChannelSelected]);

    useEffect(() => {
        if (!open) return;
        if (!isEmailChannelSelected) return;
        if (!emailTemplateQuery || emailTemplateQuery.length < 2) {
            setEmailTemplateOptions([]);
            return;
        }
        let active = true;
        setEmailTemplateLoading(true);
        searchEmailTemplates({ keyword: emailTemplateQuery })
            .then((data) => {
                if (!active) return;
                setEmailTemplateOptions(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!active) return;
                setEmailTemplateOptions([]);
            })
            .finally(() => {
                if (active) setEmailTemplateLoading(false);
            });
        return () => {
            active = false;
        };
    }, [open, emailTemplateQuery, isEmailChannelSelected]);

    const handleSubmit = async () => {
        setCreating(true);
        setCreateError('');
        try {
            const sendChannelsInt = (createForm.sendChannels || []).reduce((acc, v) => acc | Number(v), 0);
            const payload = {
                title: createForm.title,
                content: createForm.content,
                category: Number(createForm.category) || 0,
                relatedEntityId: (Number(createForm.category) === 1 || Number(createForm.category) === 2 || Number(createForm.category) === 6 || Number(createForm.category) === 8 || Number(createForm.category) === 9) ? createForm.relatedEntityId || null : null,
                sendChannels: sendChannelsInt,
                targetType: Number(createForm.targetType) || 0,
                targetData:
                    createForm.targetType === 0
                        ? null
                        : createForm.targetType === 1
                            ? createForm.targetData ? [String(createForm.targetData)] : []
                            : Array.isArray(createForm.targetData)
                                ? createForm.targetData
                                : createForm.targetData
                                    ? createForm.targetData.split(',').map((s) => s.trim()).filter(Boolean)
                                    : [],
                visibility: Number(createForm.targetType) === 1 || Number(createForm.targetType) === 2 ? 0 : Number(createForm.visibility) || 0,
                expireAt: createForm.expireAt ? new Date(`${createForm.expireAt}:00`).toISOString() : null,
                emailTemplateId: isEmailChannelSelected ? createForm.emailTemplateId || createForm.emailTemplate?.id || null : null,
                isPublished: createForm.isPublished || false,
                publishAt: createForm.publishAt ? new Date(`${createForm.publishAt}:00`).toISOString() : null,
            };
            if (Number(createForm.automationType) === 1) {
                payload.automationType = 1;
                payload.automationRule = createForm.scheduledOnceAt ? new Date(`${createForm.scheduledOnceAt}:00`).toISOString() : null;
            } else if (Number(createForm.automationType) === 2) {
                payload.automationType = 2;
                if (createForm.recurrenceType === 'weekly') {
                    payload.automationRule = `WEEKLY:${createForm.recurrenceDayOfWeek}`;
                } else if (createForm.recurrenceType === 'monthly') {
                    payload.automationRule = `MONTHLY:${Math.max(1, Math.min(31, Number(createForm.recurrenceDayOfMonth) || 1))}`;
                } else if (createForm.recurrenceType === 'yearly') {
                    const m = Math.max(1, Math.min(12, Number(createForm.recurrenceMonth) || 1));
                    const d = Math.max(1, Math.min(31, Number(createForm.recurrenceDayOfMonth) || 1));
                    const pad = (n) => String(n).padStart(2, '0');
                    payload.automationRule = `YEARLY:${pad(m)}-${pad(d)}`;
                } else {
                    payload.automationRule = null;
                }
            } else {
                payload.automationType = 0;
                payload.automationRule = null;
            }
            await createAnnouncementAction(payload, createForm.coverImage, createForm.attachments);
            toast.success('Duyuru başarıyla oluşturuldu');
            handleClose();
            if (onSuccess) onSuccess();
        } catch (e) {
            setCreateError(e?.message || 'Duyuru oluşturulamadı');
        } finally {
            setCreating(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle
                sx={{
                    fontSize: '18px',
                    fontWeight: 500,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: 3,
                }}
            >
                Yeni Duyuru
                <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ py: 2, px: 3 }}>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            Başlık
                        </Typography>
                        <TextField
                            fullWidth
                            value={createForm.title}
                            onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder=""
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '5px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#0E4D94',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    py: 1.2,
                                    px: 1.5,
                                    fontSize: '14px',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e6e6e6',
                                },
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            İçerik
                        </Typography>
                        <RichTextEditor value={createForm.content} onChange={(html) => setCreateForm((f) => ({ ...f, content: html }))} placeholder="Duyuru içeriğini buraya yazın..." />
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            Kapak Fotoğrafı
                        </Typography>
                        <input
                            key={`cover-image-${fileInputKey}`}
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="cover-image-upload"
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    setCreateForm((f) => ({ ...f, coverImage: file }));
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                        setCoverImagePreview(reader.result);
                                    };
                                    reader.readAsDataURL(file);
                                }
                            }}
                        />
                        {!createForm.coverImage ? (
                            <label htmlFor="cover-image-upload">
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        py: 2,
                                        px: 2,
                                        border: '2px dashed #e6e6e6',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        backgroundColor: '#fafafa',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            borderColor: '#0E4D94',
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                >
                                    <ImageIcon sx={{ fontSize: '20px', color: '#0E4D94' }} />
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#0E4D94', fontWeight: 500 }}>
                                        Kapak Fotoğrafı Seç
                                    </Typography>
                                </Box>
                            </label>
                        ) : (
                            <Box sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                    {coverImagePreview && (
                                        <Box
                                            component="img"
                                            src={coverImagePreview}
                                            alt="Kapak fotoğrafı önizleme"
                                            sx={{
                                                width: 120,
                                                height: 120,
                                                objectFit: 'cover',
                                                borderRadius: '5px',
                                                border: '1px solid #e6e6e6',
                                            }}
                                        />
                                    )}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5 }}>
                                            {createForm.coverImage.name}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>
                                            {(createForm.coverImage.size / 1024 / 1024).toFixed(2)} MB
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setCreateForm((f) => ({ ...f, coverImage: null }));
                                                setCoverImagePreview(null);
                                            }}
                                            sx={{
                                                backgroundColor: '#FFEEF3',
                                                borderRadius: '6px',
                                                width: 32,
                                                height: 32,
                                                '&:hover': {
                                                    backgroundColor: '#FFD6E0',
                                                },
                                                '& svg': {
                                                    fontSize: '16px',
                                                    color: '#F8285A',
                                                },
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            Ek Dosyalar
                        </Typography>
                        <input
                            key={`attachments-${fileInputKey}`}
                            accept="*/*"
                            style={{ display: 'none' }}
                            id="attachments-upload"
                            type="file"
                            multiple
                            onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                if (files.length > 0) {
                                    setCreateForm((f) => ({ ...f, attachments: [...(f.attachments || []), ...files] }));
                                }
                            }}
                        />
                        <label htmlFor="attachments-upload">
                            <Box
                                component="span"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    py: 2,
                                    px: 2,
                                    border: '2px dashed #e6e6e6',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    backgroundColor: '#fafafa',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: '#0E4D94',
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            >
                                <AttachFileIcon sx={{ fontSize: '20px', color: '#0E4D94' }} />
                                <Typography variant="body2" sx={{ fontSize: '14px', color: '#0E4D94', fontWeight: 500 }}>
                                    Dosya Ekle
                                </Typography>
                            </Box>
                        </label>
                        {createForm.attachments && createForm.attachments.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {createForm.attachments.map((file, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 1.5,
                                            py: 0.75,
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '5px',
                                            border: '1px solid #e6e6e6',
                                            maxWidth: '200px',
                                        }}
                                    >
                                        <AttachFileIcon sx={{ fontSize: '14px', color: '#0E4D94' }} />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '12px',
                                                color: '#444444',
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1,
                                            }}
                                        >
                                            {file.name}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                const newAttachments = [...createForm.attachments];
                                                newAttachments.splice(index, 1);
                                                setCreateForm((f) => ({ ...f, attachments: newAttachments }));
                                            }}
                                            sx={{
                                                backgroundColor: 'transparent',
                                                borderRadius: '4px',
                                                width: 20,
                                                height: 20,
                                                minWidth: 20,
                                                padding: 0,
                                                '&:hover': {
                                                    backgroundColor: '#FFEEF3',
                                                },
                                                '& svg': {
                                                    fontSize: '14px',
                                                    color: '#F8285A',
                                                },
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            Kategori
                        </Typography>
                        <TextField
                            fullWidth
                            select
                            value={createForm.category}
                            onChange={(e) => setCreateForm((f) => ({ ...f, category: Number(e.target.value) }))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '5px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#0E4D94',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    py: 1.2,
                                    px: 1.5,
                                    fontSize: '14px',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e6e6e6',
                                },
                            }}
                        >
                            <MenuItem value={0}>Genel</MenuItem>
                            <MenuItem value={1}>Etkinlik</MenuItem>
                            <MenuItem value={2}>Proje</MenuItem>
                            <MenuItem value={3}>Vefat</MenuItem>
                            <MenuItem value={4}>Evlilik</MenuItem>
                            <MenuItem value={5}>Doğum</MenuItem>
                            <MenuItem value={6}>Yeni Üye Katılımı</MenuItem>
                            <MenuItem value={7}>Bayram Tebrikleri</MenuItem>
                            <MenuItem value={8}>Haftalık Toplantı</MenuItem>
                            <MenuItem value={9}>Haber</MenuItem>
                        </TextField>
                    </Box>
                    {(Number(createForm.category) === 6 || createForm.category == 6) && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Üye
                            </Typography>
                            <Autocomplete
                                options={newMemberOptions}
                                loading={newMemberLoading}
                                value={createForm.relatedEntity || null}
                                onChange={(e, v) => setCreateForm((f) => ({ ...f, relatedEntityId: v?.id || v?.Id || null, relatedEntity: v || null }))}
                                onInputChange={(e, v) => setNewMemberQuery(v)}
                                filterOptions={(x) => x}
                                isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
                                getOptionLabel={(o) => o?.fullName || o?.FullName || ''}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id || option.Id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl || option.ProfilePictureUrl || option.profilePicture || option.ProfilePicture)} sx={{ width: 24, height: 24 }} />
                                        {option.fullName || option.FullName}
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Üye ara veya seç..."
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                py: 0,
                                                px: 0,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiAutocomplete-inputRoot': {
                                                py: '2.5px',
                                                px: '12px',
                                            },
                                            '& .MuiAutocomplete-input': {
                                                py: 0,
                                                px: 0,
                                                fontSize: '14px',
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                        InputProps={{ ...params.InputProps, endAdornment: <>{newMemberLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</> }}
                                    />
                                )}
                            />
                        </Box>
                    )}
                    {(Number(createForm.category) === 6 || createForm.category == 6) && createForm.relatedEntity && (
                        <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar src={resolveGroupsAssetUrl(createForm.relatedEntity.profilePictureUrl || createForm.relatedEntity.ProfilePictureUrl || createForm.relatedEntity.profilePicture || createForm.relatedEntity.ProfilePicture)} sx={{ width: 32, height: 32 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {createForm.relatedEntity.fullName || createForm.relatedEntity.FullName}
                                </Typography>
                            </Stack>
                            <Button size="small" onClick={() => setCreateForm((f) => ({ ...f, relatedEntityId: null, relatedEntity: null }))}>
                                Kaldır
                            </Button>
                        </Paper>
                    )}
                    {Number(createForm.category) === 1 && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Etkinlik
                            </Typography>
                            <Autocomplete
                                options={eventOptions}
                                loading={eventLoading}
                                value={createForm.relatedEntity || null}
                                onChange={(e, v) => setCreateForm((f) => ({ ...f, relatedEntityId: v?.id || null, relatedEntity: v || null }))}
                                onInputChange={(e, v) => setEventQuery(v)}
                                filterOptions={(x) => x}
                                isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
                                getOptionLabel={(o) => (o?.title ? `${o.title} ${o.city ? `- ${o.city}` : ''}` : '')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder=""
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                py: 0,
                                                px: 0,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiAutocomplete-inputRoot': {
                                                py: '2.5px',
                                                px: '12px',
                                            },
                                            '& .MuiAutocomplete-input': {
                                                py: 0,
                                                px: 0,
                                                fontSize: '14px',
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                        InputProps={{ ...params.InputProps, endAdornment: <>{eventLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</> }}
                                    />
                                )}
                            />
                        </Box>
                    )}
                    {Number(createForm.category) === 1 && createForm.relatedEntity && (
                        <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Stack spacing={0.2}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {createForm.relatedEntity.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {createForm.relatedEntity.city || '-'}
                                    {createForm.relatedEntity.startDate ? ` • ${new Date(createForm.relatedEntity.startDate).toLocaleString('tr-TR')}` : ''}
                                </Typography>
                            </Stack>
                            <Button size="small" onClick={() => setCreateForm((f) => ({ ...f, relatedEntityId: null, relatedEntity: null }))}>
                                Kaldır
                            </Button>
                        </Paper>
                    )}
                    {Number(createForm.category) === 2 && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Proje
                            </Typography>
                            <Autocomplete
                                options={projectOptions}
                                loading={projectLoading}
                                value={createForm.relatedEntity || null}
                                onChange={(e, v) => setCreateForm((f) => ({ ...f, relatedEntityId: v?.id || null, relatedEntity: v || null }))}
                                onInputChange={(e, v) => setProjectQuery(v)}
                                isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
                                getOptionLabel={(o) => (o?.name || o?.title || '')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder=""
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                py: 0,
                                                px: 0,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiAutocomplete-inputRoot': {
                                                py: '2.5px',
                                                px: '12px',
                                            },
                                            '& .MuiAutocomplete-input': {
                                                py: 0,
                                                px: 0,
                                                fontSize: '14px',
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                        InputProps={{ ...params.InputProps, endAdornment: <>{projectLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</> }}
                                    />
                                )}
                            />
                        </Box>
                    )}
                    {Number(createForm.category) === 2 && createForm.relatedEntity && (
                        <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Stack spacing={0.2}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {createForm.relatedEntity.name || createForm.relatedEntity.title}
                                </Typography>
                                {createForm.relatedEntity.description && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {createForm.relatedEntity.description}
                                    </Typography>
                                )}
                            </Stack>
                            <Button size="small" onClick={() => setCreateForm((f) => ({ ...f, relatedEntityId: null, relatedEntity: null }))}>
                                Kaldır
                            </Button>
                        </Paper>
                    )}
                    {Number(createForm.category) === 8 && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Haftalık Toplantı
                            </Typography>
                            <Autocomplete
                                options={meetingOptions}
                                loading={meetingLoading}
                                value={createForm.relatedEntity || null}
                                onChange={(e, v) => setCreateForm((f) => ({ ...f, relatedEntityId: v?.id || null, relatedEntity: v || null }))}
                                onInputChange={(e, v) => setMeetingQuery(v)}
                                filterOptions={(x) => x}
                                isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
                                getOptionLabel={(o) => (o?.title ? `${o.title}${o.meetingDate ? ` - ${new Date(o.meetingDate).toLocaleDateString('tr-TR')}` : ''}` : '')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder=""
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                py: 0,
                                                px: 0,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiAutocomplete-inputRoot': {
                                                py: '2.5px',
                                                px: '12px',
                                            },
                                            '& .MuiAutocomplete-input': {
                                                py: 0,
                                                px: 0,
                                                fontSize: '14px',
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                        InputProps={{ ...params.InputProps, endAdornment: <>{meetingLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</> }}
                                    />
                                )}
                            />
                        </Box>
                    )}
                    {Number(createForm.category) === 8 && createForm.relatedEntity && (
                        <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Stack spacing={0.2}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {createForm.relatedEntity.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {createForm.relatedEntity.meetingDate ? new Date(createForm.relatedEntity.meetingDate).toLocaleString('tr-TR') : ''}
                                    {createForm.relatedEntity.location ? ` • ${createForm.relatedEntity.location}` : ''}
                                </Typography>
                            </Stack>
                            <Button size="small" onClick={() => setCreateForm((f) => ({ ...f, relatedEntityId: null, relatedEntity: null }))}>
                                Kaldır
                            </Button>
                        </Paper>
                    )}
                    {Number(createForm.category) === 9 && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Haber
                            </Typography>
                            <Autocomplete
                                options={newsOptions}
                                loading={newsLoading}
                                value={createForm.relatedEntity || null}
                                onChange={(e, v) => setCreateForm((f) => ({ ...f, relatedEntityId: v?.id || null, relatedEntity: v || null }))}
                                onInputChange={(e, v) => setNewsQuery(v)}
                                isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
                                getOptionLabel={(o) => (o?.title || '')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder=""
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                py: 0,
                                                px: 0,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiAutocomplete-inputRoot': {
                                                py: '2.5px',
                                                px: '12px',
                                            },
                                            '& .MuiAutocomplete-input': {
                                                py: 0,
                                                px: 0,
                                                fontSize: '14px',
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                        InputProps={{ ...params.InputProps, endAdornment: <>{newsLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</> }}
                                    />
                                )}
                            />
                        </Box>
                    )}
                    {Number(createForm.category) === 9 && createForm.relatedEntity && (
                        <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Stack spacing={0.2}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {createForm.relatedEntity.title}
                                </Typography>
                                {createForm.relatedEntity.summary && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {createForm.relatedEntity.summary}
                                    </Typography>
                                )}
                            </Stack>
                            <Button size="small" onClick={() => setCreateForm((f) => ({ ...f, relatedEntityId: null, relatedEntity: null }))}>
                                Kaldır
                            </Button>
                        </Paper>
                    )}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            Hedef Tipi
                        </Typography>
                        <TextField
                            fullWidth
                            select
                            value={createForm.targetType}
                            onChange={(e) => setCreateForm((f) => ({ ...f, targetType: Number(e.target.value), targetData: '' }))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '5px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#0E4D94',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    py: 1.2,
                                    px: 1.5,
                                    fontSize: '14px',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e6e6e6',
                                },
                            }}
                        >
                            <MenuItem value={0}>Tüm Üyeler</MenuItem>
                            <MenuItem value={1}>Belirli Gruplar</MenuItem>
                            <MenuItem value={2}>Belirli Üyeler</MenuItem>
                        </TextField>
                    </Box>
                    {Number(createForm.targetType) === 1 ? (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Grup
                            </Typography>
                            <Autocomplete
                                options={groupOptions}
                                loading={groupLoading}
                                onChange={(e, v) => setCreateForm((f) => ({ ...f, targetData: v?.id ? String(v.id) : '' }))}
                                onInputChange={(e, v) => setGroupQuery(v)}
                                filterOptions={(x) => x}
                                getOptionLabel={(o) => (o?.name ? `${o.name} ${typeof o.memberCount === 'number' ? `(${o.memberCount})` : ''}` : '')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder=""
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                py: 0,
                                                px: 0,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiAutocomplete-inputRoot': {
                                                py: '2.5px',
                                                px: '12px',
                                            },
                                            '& .MuiAutocomplete-input': {
                                                py: 0,
                                                px: 0,
                                                fontSize: '14px',
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                        InputProps={{ ...params.InputProps, endAdornment: <>{groupLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</> }}
                                    />
                                )}
                            />
                        </Box>
                    ) : Number(createForm.targetType) === 2 ? (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Üye
                            </Typography>
                            <Autocomplete
                                multiple
                                options={memberOptions}
                                loading={memberLoading}
                                value={Array.isArray(selectedMembers) ? selectedMembers : []}
                                onChange={(e, v) => {
                                    setSelectedMembers(v || []);
                                    setCreateForm((f) => ({ ...f, targetData: (v || []).map((m) => m.id || m.Id) }));
                                }}
                                onInputChange={(e, v) => setMemberQuery(v)}
                                filterOptions={(x) => x}
                                isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
                                getOptionLabel={(o) => o?.fullName || o?.FullName || ''}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id || option.Id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl || option.ProfilePictureUrl || option.profilePicture || option.ProfilePicture)} sx={{ width: 24, height: 24 }} />
                                        {option.fullName || option.FullName}
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder=""
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                py: 0,
                                                px: 0,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiAutocomplete-inputRoot': {
                                                py: '2.5px',
                                                px: '12px',
                                            },
                                            '& .MuiAutocomplete-input': {
                                                py: 0,
                                                px: 0,
                                                fontSize: '14px',
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                        InputProps={{ ...params.InputProps, endAdornment: <>{memberLoading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</> }}
                                    />
                                )}
                            />
                        </Box>
                    ) : (
                        Number(createForm.targetType) !== 0 && (
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                    Hedef Verisi (virgülle)
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="userId1, userId2"
                                    value={createForm.targetData}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, targetData: e.target.value }))}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '5px',
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#0E4D94',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            py: 1.2,
                                            px: 1.5,
                                            fontSize: '14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#e6e6e6',
                                        },
                                    }}
                                />
                            </Box>
                        )
                    )}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            Görünürlük
                        </Typography>
                        <TextField
                            fullWidth
                            select
                            value={createForm.visibility}
                            onChange={(e) => setCreateForm((f) => ({ ...f, visibility: Number(e.target.value) }))}
                            disabled={Number(createForm.targetType) === 1 || Number(createForm.targetType) === 2}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '5px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#0E4D94',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    py: 1.2,
                                    px: 1.5,
                                    fontSize: '14px',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e6e6e6',
                                },
                            }}
                        >
                            <MenuItem value={0}>Sadece Üyeler</MenuItem>
                            <MenuItem value={1}>Herkese Açık</MenuItem>
                        </TextField>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            Yayınla
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={createForm.isPublished}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setCreateForm((f) => {
                                            const next = { ...f, isPublished: checked };
                                            if (checked && !f.publishAt) {
                                                const now = new Date();
                                                const pad = (n) => String(n).padStart(2, '0');
                                                next.publishAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
                                            }
                                            return next;
                                        });
                                    }}
                                />
                            }
                            label=""
                        />
                    </Box>
                    {createForm.isPublished && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Yayın Tarihi
                            </Typography>
                            <TextField
                                type="datetime-local"
                                fullWidth
                                value={createForm.publishAt}
                                onChange={(e) => setCreateForm((f) => ({ ...f, publishAt: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            />
                        </Box>
                    )}
                    <Stack>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Yayın Kanalları
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }}>
                            {[
                                { label: 'E-posta', val: 1 },
                                { label: 'SMS', val: 2 },
                                { label: 'Mobil Uygulama Bildirimi', val: 4 },
                                { label: 'Mobil Uygulama', val: 8 },
                            ].map((opt) => (
                                <FormControlLabel
                                    key={opt.val}
                                    control={
                                        <Checkbox
                                            checked={createForm.sendChannels.includes(opt.val)}
                                            onChange={(e) => {
                                                setCreateForm((f) => {
                                                    const sel = new Set(f.sendChannels);
                                                    if (e.target.checked) sel.add(opt.val);
                                                    else sel.delete(opt.val);
                                                    const nextChannels = Array.from(sel);
                                                    const nextState = { ...f, sendChannels: nextChannels };
                                                    if (!sel.has(1)) {
                                                        nextState.emailTemplateId = null;
                                                        nextState.emailTemplate = null;
                                                    }
                                                    return nextState;
                                                });
                                            }}
                                        />
                                    }
                                    label={opt.label}
                                />
                            ))}
                        </Stack>
                    </Stack>
                    {isEmailChannelSelected && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                E-posta Şablonu
                            </Typography>
                            <Autocomplete
                                options={emailTemplateOptions}
                                loading={emailTemplateLoading}
                                value={createForm.emailTemplate || null}
                                onChange={(e, val) =>
                                    setCreateForm((f) => ({
                                        ...f,
                                        emailTemplate: val || null,
                                        emailTemplateId: val?.id || null,
                                    }))
                                }
                                onInputChange={(e, val) => setEmailTemplateQuery(val)}
                                filterOptions={(x) => x}
                                isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
                                getOptionLabel={(o) => o?.name || ''}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder=""
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                py: 0,
                                                px: 0,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiAutocomplete-inputRoot': {
                                                py: '2.5px',
                                                px: '12px',
                                            },
                                            '& .MuiAutocomplete-input': {
                                                py: 0,
                                                px: 0,
                                                fontSize: '14px',
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {emailTemplateLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            {createForm.emailTemplate?.subject && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Seçilen konu: {createForm.emailTemplate.subject}
                                </Typography>
                            )}
                        </Box>
                    )}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            Otomasyon
                        </Typography>
                        <TextField
                            fullWidth
                            select
                            value={createForm.automationType}
                            onChange={(e) => setCreateForm((f) => ({ ...f, automationType: Number(e.target.value) }))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '5px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#0E4D94',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    py: 1.2,
                                    px: 1.5,
                                    fontSize: '14px',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e6e6e6',
                                },
                            }}
                        >
                            <MenuItem value={0}>Manuel</MenuItem>
                            <MenuItem value={1}>Tek Sefer (Zamanlı)</MenuItem>
                            <MenuItem value={2}>Tekrarlayan</MenuItem>
                        </TextField>
                    </Box>
                    {Number(createForm.automationType) === 1 && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Yayınlanacak Tarih
                            </Typography>
                            <TextField
                                type="datetime-local"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={createForm.scheduledOnceAt}
                                onChange={(e) => setCreateForm((f) => ({ ...f, scheduledOnceAt: e.target.value }))}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            />
                        </Box>
                    )}
                    {Number(createForm.automationType) === 2 && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                    Tekrar
                                </Typography>
                                <TextField
                                    fullWidth
                                    select
                                    value={createForm.recurrenceType}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, recurrenceType: e.target.value }))}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '5px',
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#0E4D94',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            py: 1.2,
                                            px: 1.5,
                                            fontSize: '14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#e6e6e6',
                                        },
                                    }}
                                >
                                    <MenuItem value="weekly">Haftada bir</MenuItem>
                                    <MenuItem value="monthly">Ayda bir</MenuItem>
                                    <MenuItem value="yearly">Yılda bir</MenuItem>
                                </TextField>
                            </Box>
                            {createForm.recurrenceType === 'weekly' && (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                        Gün
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        select
                                        value={createForm.recurrenceDayOfWeek}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, recurrenceDayOfWeek: e.target.value }))}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                    >
                                        <MenuItem value="MON">Pazartesi</MenuItem>
                                        <MenuItem value="TUE">Salı</MenuItem>
                                        <MenuItem value="WED">Çarşamba</MenuItem>
                                        <MenuItem value="THU">Perşembe</MenuItem>
                                        <MenuItem value="FRI">Cuma</MenuItem>
                                        <MenuItem value="SAT">Cumartesi</MenuItem>
                                        <MenuItem value="SUN">Pazar</MenuItem>
                                    </TextField>
                                </Box>
                            )}
                            {createForm.recurrenceType === 'monthly' && (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                        Ayın Kaçıncı Günü
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 1, max: 31 }}
                                        value={createForm.recurrenceDayOfMonth}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, recurrenceDayOfMonth: Math.max(1, Math.min(31, Number(e.target.value) || 1)) }))}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '5px',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0E4D94',
                                                },
                                            },
                                            '& .MuiInputBase-input': {
                                                py: 1.2,
                                                px: 1.5,
                                                fontSize: '14px',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e6e6e6',
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                            {createForm.recurrenceType === 'yearly' && (
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                            Ay
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            select
                                            value={createForm.recurrenceMonth}
                                            onChange={(e) => setCreateForm((f) => ({ ...f, recurrenceMonth: Number(e.target.value) }))}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '5px',
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#0E4D94',
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    py: 1.2,
                                                    px: 1.5,
                                                    fontSize: '14px',
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#e6e6e6',
                                                },
                                            }}
                                        >
                                            <MenuItem value={1}>Ocak</MenuItem>
                                            <MenuItem value={2}>Şubat</MenuItem>
                                            <MenuItem value={3}>Mart</MenuItem>
                                            <MenuItem value={4}>Nisan</MenuItem>
                                            <MenuItem value={5}>Mayıs</MenuItem>
                                            <MenuItem value={6}>Haziran</MenuItem>
                                            <MenuItem value={7}>Temmuz</MenuItem>
                                            <MenuItem value={8}>Ağustos</MenuItem>
                                            <MenuItem value={9}>Eylül</MenuItem>
                                            <MenuItem value={10}>Ekim</MenuItem>
                                            <MenuItem value={11}>Kasım</MenuItem>
                                            <MenuItem value={12}>Aralık</MenuItem>
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                            Gün
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            inputProps={{ min: 1, max: 31 }}
                                            value={createForm.recurrenceDayOfMonth}
                                            onChange={(e) => setCreateForm((f) => ({ ...f, recurrenceDayOfMonth: Math.max(1, Math.min(31, Number(e.target.value) || 1)) }))}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '5px',
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#0E4D94',
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    py: 1.2,
                                                    px: 1.5,
                                                    fontSize: '14px',
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#e6e6e6',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Stack>
                            )}
                        </Stack>
                    )}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                            Yayın Bitiş Tarihi
                        </Typography>
                        <TextField
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={createForm.expireAt}
                            onChange={(e) => setCreateForm((f) => ({ ...f, expireAt: e.target.value }))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '5px',
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#0E4D94',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    py: 1.2,
                                    px: 1.5,
                                    fontSize: '14px',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e6e6e6',
                                },
                            }}
                        />
                    </Box>
                    {createError && (
                        <Box sx={{ color: 'error.main', fontSize: 13 }}>
                            {createError}
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ py: 2, px: 3 }}>
                <Button onClick={handleClose} disabled={creating} sx={{ textTransform: 'none', color: '#0E4D94' }}>
                    İptal
                </Button>
                <Button
                    variant="contained"
                    disabled={creating || !createForm.title || !createForm.content || (Number(createForm.automationType) === 1 && !createForm.scheduledOnceAt)}
                    onClick={handleSubmit}
                    sx={{
                        borderRadius: '5px',
                        textTransform: 'none',
                        fontSize: '14px',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                    }}
                >
                    {creating ? 'Oluşturuluyor...' : 'Oluştur'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateAnnouncementDialog;
